import { RecycleScroller } from 'vue-virtual-scroller';
import TenSelectOption from './select-option';
import TenSelectOptionGroup from './select-option-group';
import TenInputSelect from '../input/input-select';
import TenPopup from '../popup';
import TenIcon from '../icon';
import TenLoading from '../loading';
import { isEmpty, removeUndefinedAttr } from '../scripts/utils/helper';

const name = 'ten-select';
export default {
  name,
  provide() {
    return {
      select: this,
      tenFormItem: undefined,
    };
  },
  inject: {
    formItem: {
      from: 'tenFormItem',
      default: null,
    },
  },
  components: {
    RecycleScroller,
  },
  props: {
    /**
     * v-model 当前值，对应事件 change
     */
    value: [String, Number, Array, Object, Boolean],
    /**
     * 非受控时，可传入默认值
     */
    defaultValue: [String, Number, Array, Object, Boolean],
    /**
     * 选项数据源
     * 支持{label, value}对象数组, 也支持字符串数组
     * 或者使用optionValue, optionLabel
     */
    dataSource: Array,
    /**
     * value: 选中值为选项的value值
     * data: 选中值为选项的data值，需要在select-option中传入data，并指定正确的键值optionValue、optionLabel
     * @member value|data
     */
    valueType: {
      type: String,
      default: 'value',
      validator: v => ['value', 'data'].indexOf(v) > -1,
    },
    /**
     * 选项为对象时，指定选项的value在data中的键值。
     * 设置为函数时，参数为data，函数返回值将指定为value。
     * 设置value-type="data"或者使用data-source可能需要设置。
     */
    optionValue: {
      type: [String, Function],
      default: 'value',
    },
    /**
     * 选项为对象时，指定选项的label在data中的键值。
     * 可设置为函数时，参数为data，函数返回值将指定为label。
     * 设置value-type="data"或者使用data-source可能需要设置。
     */
    optionLabel: {
      type: [String, Function],
      default: 'label',
    },
    /**
     * 使用数据源 datasource 时也可以自定义渲染函数
     */
    renderOption: Function,
    /**
     * 单选模式，是否可以清空
     */
    clearable: Boolean,
    /**
     * 禁用
     */
    disabled: Boolean,
    /**
     * 只读
     */
    readonly: Boolean,
    /**
     * 是否多选
     */
    multiple: Boolean,
    /**
     * 输入提示
     */
    placeholder: {
      type: String,
      default: '请选择',
    },
    /**
     * 大小
     * @defaultValue default
     * @member default|small
     */
    size: {
      type: String,
      default: 'default',
    },
    /**
     * 多选模式下是否在选项前显示 checkbox
     */
    optionCheckbox: {
      type: Boolean,
      default: true,
    },
    /**
     * 后缀图标
     * @defaultValue carret_down
     */
    suffixIcon: String,
    /**
     * 设置为 true 则根据 optionLabel 的值自动筛选 默认忽略大小写
     * 设置为 false 不作过滤操作
     * 可以是函数，自定义筛选规则
     * (filterValue, value) => {}
     */
    filterOption: {
      type: [Boolean, Function],
      default: false,
    },
    /**
     * 如果筛选选项为空，是否创建，触发create事件
     */
    creatable: Boolean,
    /**
     * 筛选模式下，选择选项之后是否清空输入框
     */
    clearInputAfterSelect: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否加载中，异步获取选项时适用
     */
    loading: Boolean,
    /**
     * 结果为空时提示文字，支持同名slot
     */
    emptyTip: { type: String, default: '暂无数据' },
    /**
     * 弹框 class
     */
    popupClass: [String, Object, Array],
    /**
     * 多选时是否将选中值按文字的形式展示
     */
    collapseTags: Boolean,
    /**
     * 虚拟滚动配置项，参考 vue-virtual-scroller 库
     */
    recycleScrollOptions: {
      type: Object,
      default: () => ({}),
    },
    /**
     * 展示全选按钮，只在多选模式且筛选模式下有效
     */
    showSelectAll: Boolean,
  },
  data() {
    return {
      popupShow: false,
      filterValue: '',
      focusIndex: -1,
      isEmpty: true,
      focusLabel: null,
    };
  },
  computed: {
    _class() {
      return [
        'ten-select',
        'ten-select-input',
        {
          'ten-select-input--popupshow': this.popupShow,
        },
      ];
    },
    _popupClass() {
      return [
        'ten-select__popup',
        'ten-select-options',
        `ten-select-options--size-${this.size}`,
        {
          'ten-select__popup--loading': this.loading,
        },
        this.popupClass,
      ];
    },
    _creatableClass() {
      return [
        'ten-select__create',
        'ten-select-option',
        {
          'ten-select-option--focused': this.focusIndex === 0,
        },
      ];
    },
    _isFilteringMode() {
      return !!this.filterOption || !!this.$listeners.filter;
    },
    _suffixIcon() {
      return this.suffixIcon || 'carret_down';
    },
    _readonly() {
      return this.readonly || !(this._isFilteringMode && this.popupShow);
    },
    _inputProps() {
      return removeUndefinedAttr({
        prefix: this.$attrs.prefix,
        suffix: this.$attrs.suffix,
      });
    },
    _trimmedFilterValue() {
      return this.filterValue.trim();
    },
    _isPrependCustomOption() {
      if (!this.$scopedSlots.default) return false;
      const index = this.$scopedSlots.default()
        .findIndex(vNode => !vNode.componentOptions || vNode.componentOptions.tag !== 'ten-select-option');
      return index === 0;
    }
  },
  created() {
    this.dataValue = undefined;
  },
  mounted() {
    this.isBlur = true;
    this.$on('option:click', this.onOptionClick);
    this.$on('option:focus', this.onOptionFocus);
  },
  render(h) {
    const options = this.getOptions();
    const filteredOptions = this.getFilteredOptions(options, this._trimmedFilterValue);
    const customOptions = this.getCustomOptions();
    const groupOptions = this.getGroupOptions();

    this.isEmpty = !filteredOptions.length && !customOptions.length;
    this._isCreateShow = this.creatable
      && !this.loading
      && this._trimmedFilterValue
      && !filteredOptions.find(option => option._label === this._trimmedFilterValue);
    this.dataValue = this.transform(this.value, options);

    filteredOptions.forEach((option, i) => {
      option._index = i + (this._isCreateShow ? 1 : 0);
    });

    const trigger = () => (
      <TenInputSelect
        ref="input"
        class={this._class}
        placeholder={this.placeholder}
        size={this.size}
        clearable={this.clearable}
        disabled={this.disabled}
        readonly={this._readonly}
        value={this.dataValue}
        multiple={this.multiple}
        collapseTags={this.collapseTags}
        is-filtering-mode={this._isFilteringMode}
        get-input={this.getInput}
        get-value-key={v => v._value}
        get-value-text={v => v._label}
        get-value-disabled={v => v._disabled || this.readonly}
        {
          ...{
            on: {
              'tag-delete': this.onTagDelete,
              text: this.onInputText,
              click: this.onTriggerClick,
              keydown: this.onSelectKeyDown,
              focus: this.inputFocus,
              blur: this.selectBlur,
              clear: this.onClear,
            },
            attrs: this._inputProps,
          }
        }
      >
        <TenIcon
          slot="suffixIcon"
          icon={this._suffixIcon}
          onClick={this.onSuffixIconClick}
        />
      </TenInputSelect>
    );

    return (
      <TenPopup
        trigger-type="manual"
        direction="bottom left"
        destroy={false}
        show={this.popupShow}
        width={this.popupWidthHandler}
        popup-class={this._popupClass}
        onChange={() => this.popupShow = false}
        scopedSlots={{ trigger }}
      >
        <div
          role="listbox"
          class="ten-select__popup-content"
          ref="refPopup"
        >
          {
            this._isCreateShow
              ? (
                <div
                  ref="createOption"
                  class={this._creatableClass}
                  onClick={this.onCreateHandler}
                >
                  {
                    this.$scopedSlots.create
                      ? this.$scopedSlots.create({ filterValue: this.filterValue }) : `创建 ${this.filterValue}`
                  }
                </div>
              ) : null
          }
          { this._isPrependCustomOption && customOptions }

          <div class="ten-select-options-wrapper">
            { this.renderOptions(h, groupOptions, filteredOptions) }
          </div>
          {
            !this.loading && this.isEmpty
              ? (
                <div
                class="ten-select__empty"
                >
                  {
                    this.$scopedSlots.emptyTip
                      ? this.$scopedSlots.emptyTip() : this.emptyTip
                  }
                </div>
              ) : null
          }
          { this.loading && <TenLoading type="block" /> }
          { !this._isPrependCustomOption && customOptions }
        </div>
      </TenPopup>
    );
  },
  mounted() {
    this.isBlur = true;
    this.$on('option:click', this.onOptionClick);
    this.$on('option:focus', this.onOptionFocus);
  },
  methods: {
    renderOptions(h, groupOptions, filteredOptions) {
      if (groupOptions.length && !this._trimmedFilterValue) {
        return groupOptions.map(group => this.renderSelectOptionGroup(h, group));
      }

      if (filteredOptions.length < 500) {
        const { multiple, _isCreateShow, isEmpty, filterOption } = this;
        const _showSelectAll = multiple && !_isCreateShow && !isEmpty && filterOption && this.showSelectAll;
        return (
          <div className="ten-select-options">
            {
              _showSelectAll && (
                <TenSelectOption
                  value='_all'
                  label='全选'
                  isSelectAll
                  isChecked={filteredOptions.every(this.isOptionChecked)}
                  nativeOnClick={() => {
                    const checkedOptions = filteredOptions.filter(this.isOptionChecked);
                    const isCheckedAll = checkedOptions.length === filteredOptions.length;
                    this.$emit('change', this.reverse(isCheckedAll ? [] : filteredOptions));
                    this.refInput && this.refInput.focus();
                    if (this._isFilteringMode && this.clearInputAfterSelect) {
                      this.$nextTick(() => {
                        this.$refs.input && this.$refs.input.clearText();
                        this.onInputText('');
                      });
                    }
                  }}
                />
              )
            }
            { filteredOptions.map(item => this.renderSelectOption(h, item)) }
          </div>
        );
      }

      const recycleScrollOptions = {
        itemSize: 50,
        keyField: '_value',
        items: filteredOptions,
        ...this.recycleScrollOptions,
      };

      // 超长选项才启动虚拟滚动
      return (
        <RecycleScroller
          class="ten-select-options"
          { ...{ props: recycleScrollOptions } }
          scopedSlots={{
            default: ({ item }) => this.renderSelectOption(h, item),
          }}
        />);
    },
    renderSelectOption(_h, item) {
      const { _vNode: vNode } = item;
      const isChecked = this.dataValue && !!this.dataValue.find(v => v && v._value === item._value);

      if (vNode && vNode._parentKey && !this._trimmedFilterValue) return null;

      if (vNode) {
        vNode.componentOptions.propsData.isChecked = isChecked;
        vNode.componentOptions.propsData.index = item._index;
        return vNode;
      }

      return (
        <TenSelectOption
          key={item._value}
          value={item._value}
          label={item._label}
          index={item._index}
          optionObj={item}
          disabled={item._disabled}
          isChecked={isChecked}
        ></TenSelectOption>
      );
    },
    renderSelectOptionGroup(_h, group) {
      return (
        <TenSelectOptionGroup key={group.key} label={group.componentOptions.propsData.label}>
          {
            group.componentOptions.children.map((vNode) => {
              const isChecked = this.dataValue && !!this.dataValue.find(v => v && v._value === vNode._value);

              vNode.componentOptions.propsData.isChecked = isChecked;
              vNode.componentOptions.propsData.index = vNode._index;
              return vNode;
            })
          }
        </TenSelectOptionGroup>
      );
    },
    getOptions() {
      if (this.dataSource) return this.dataSource.map(d => this.createOptionObj(d));

      const options = [];
      if (this.$scopedSlots.default) {
        this.$scopedSlots.default().forEach((vNode, index) => {
          if (vNode.componentOptions && vNode.componentOptions.tag === 'ten-select-option-group') {
            vNode.componentOptions.children.forEach((item, i) => {
              item._parentKey = vNode.key;
              // gourp 从 10000 开始标记顺序
              const optionObj = this.normalizeOption(item, (10000 * index) + i);
              options.push(optionObj);
            });
          } else if (vNode.componentOptions && vNode.componentOptions.tag === 'ten-select-option') {
            const optionObj = this.normalizeOption(vNode, index);
            options.push(optionObj);
          }
        });
      }
      return options;
    },
    getCustomOptions() {
      if (!this.$scopedSlots.default) return [];
      return this.$scopedSlots.default()
        .filter(vNode => !vNode.componentOptions || (vNode.componentOptions.tag !== 'ten-select-option' && vNode.componentOptions.tag !== 'ten-select-option-group'));
    },
    getGroupOptions() {
      if (!this.$scopedSlots.default) return [];
      const groupOptions = this.$scopedSlots.default()
        .filter(vNode => vNode.componentOptions && vNode.componentOptions.tag === 'ten-select-option-group');

      groupOptions.forEach((vNode, index) => {
        vNode.componentOptions.children.forEach((item, i) => {
          // gourp 从 10000 开始标记顺序
          const optionObj = this.normalizeOption(item, (10000 * index) + i);
          Object.assign(item, optionObj);
        });
      });

      return groupOptions;
    },
    normalizeOption(vNode, i) {
      const { _parentKey } = vNode;
      const { propsData } = vNode.componentOptions;
      const { value, label, data, disabled } = propsData;
      propsData.label = this.getLabel(label, value);

      const optionObj = {
        _parentKey,
        _value: value,
        _label: propsData.label,
        _data: data || value,
        _disabled: disabled,
        _vNode: vNode,
        _index: i,
      };

      propsData.optionObj = optionObj;
      propsData.index = i;

      return optionObj;
    },
    getFilteredOptions(options, fitlerValue) {
      return options.filter((option) => {
        if (fitlerValue) {
          if (typeof this.filterOption === 'function') {
            return this.filterOption(fitlerValue, option._label);
          }
          if (
            this.filterOption
            && typeof option._label === 'string'
          ) {
            const filterList = fitlerValue.split(',');
            return filterList.some(value => option._label.toLowerCase().indexOf(value.toLowerCase()) > -1);
          }
        }
        return true;
      }).map((option, i) => {
        const filteredOption = option;
        filteredOption._index = i;
        return filteredOption;
      });
    },
    getOptionElems() {
      if (this.$refs.refPopup) {
        return Array.from(this.$refs.refPopup.querySelectorAll('.ten-select-option'))
          .filter((optionElem) => {
            const { parentNode } = optionElem;
            // filter virtual scroll dom 缓存
            if (parentNode.className.indexOf('vue-recycle-scroller') === -1) {
              return true;
            }
            return parentNode && parentNode.style.transform && parentNode.style.transform.indexOf('-9999px') === -1;
          });
      }
      return [];
    },
    transform(value, options) {
      let dataValue = value;
      if (!this.multiple || (this.multiple && !Array.isArray(dataValue))) {
        // 全部转成数组格式
        dataValue = !isEmpty(value) ? [value] : [];
      }
      if (this.valueType === 'data') {
        dataValue = dataValue.map(d => this.createOptionObj(d));
      } else if (this.valueType === 'value') {
        dataValue = dataValue
          .map((d) => {
            if (d !== null && typeof d !== 'undefined') {
              return options.find(o => o && o._value === d);
            }
            return undefined;
          })
          .filter(v => v);
      }
      return dataValue;
    },
    reverse(dataValue) {
      const value = dataValue.map(d => d[`_${this.valueType}`]);
      return this.multiple ? value : value[0];
    },
    initOptions(dataSource = []) {
      return dataSource.map(d => this.createOptionObj(d));
    },
    popupWidthHandler(input) {
      return input.offsetWidth;
    },
    getLabel(_label, value) {
      let label = _label;
      if (
        label === ''
        || typeof label === 'undefined'
        || label === null) {
        label = value;
      }

      if (typeof label === 'undefined' || label === null) label = '';

      return String(label);
    },
    createOptionObj(option, index) {
      if (option !== null && typeof option === 'object') {
        const _value = typeof this.optionValue === 'function'
          ? this.optionValue(option)
          : option[this.optionValue];
        const _label = typeof this.optionLabel === 'function'
          ? this.optionLabel(option)
          : option[this.optionLabel];

        return {
          _value,
          _label: this.getLabel(_label, _value),
          _data: option,
          _disabled: option.disabled || false,
          _index: index,
        };
      }
      return { _value: option, _label: this.getLabel(option, option), _data: option, _disabled: false, _index: index };
    },
    onCreateHandler() {
      /**
       * 点击新创建选项触发事件
       *
       * @event create
       * @param {String} filterValue
       */
      this.$emit('create', this._trimmedFilterValue);
      if (this.multiple) {
        this.filterValue = '';
        this.$refs.input && this.$refs.input.clearText();
        this.refInput && this.refInput.focus();
      } else {
        this.popupShow = false;
        setTimeout(() => {
          this.filterValue = '';
        }, 300);
      }
    },
    onOptionClick(selectOption) {
      let newValue;
      let toUpdate = true;
      const { isChecked } = selectOption;
      if (this.multiple) {
        newValue = this.dataValue.map(v => v);
        if (isChecked) {
          const index = newValue.findIndex(v => v._value === selectOption.value);
          if (index === -1) {
            toUpdate = false;
          } else {
            newValue.splice(index, 1);
          }
        } else {
          newValue.push(selectOption.optionObj);
        }
      } else if (isChecked) {
        toUpdate = false;
      } else {
        newValue = [selectOption.optionObj];
      }

      this.refInput && this.refInput.focus();
      if (this._isFilteringMode) {
        if (!this.multiple) {
          setTimeout(() => {
            this.onInputText('');
          }, 300);
        } else if (this.clearInputAfterSelect) {
          this.$nextTick(() => {
            this.$refs.input && this.$refs.input.clearText();
            this.onInputText('');
          });
        }
      }
      if (!this.multiple) this.popupShow = false;
      if (toUpdate) {
        this.$emit('change', this.reverse(newValue));
        this.$nextTick(() => {
          this.formItem && this.formItem.$emit('form.change');
        });
      } else if (this._isFilteringMode && !this.multiple) {
        this.$refs.input && this.$refs.input.setTextFromPropValue(this.dataValue);
      }
    },
    onOptionFocus(focusOption) {
      this.focusIndex = focusOption.index;
      this.focusLabel = focusOption.label;
    },
    onTagDelete(tagToDelete, afterDeleteTags) {
      this.$emit('change', this.reverse(afterDeleteTags));
    },
    onInputText(value) {
      this.filterValue = value;
      this.focusIndex = this._trimmedFilterValue ? 0 : -1;
      /**
       * 用户筛选时触发事件
       *
       * @event filter
       * @param {String} filterValue
       */
      this.$emit('filter', value);
    },
    onSelectKeyDown(e) {
      const options = this.getOptions();
      const filteredOptions = this.getFilteredOptions(options, this._trimmedFilterValue);
      const { popupShow, focusIndex } = this;
      const optionElems = this.getOptionElems();
      const total = optionElems.length;

      const { keyCode } = e;

      if (popupShow) {
        switch (keyCode) {
          case 13:
            if (focusIndex < total && focusIndex >= 0) {
              optionElems[focusIndex] && optionElems[focusIndex].click();
            }
            break;
          case 38: // up
            this.focusIndex = focusIndex <= 0 ? total - 1 : focusIndex - 1;
            this.focusLabel = filteredOptions[this.focusIndex]._label;
            e.preventDefault();
            break;
          case 40: // down
            this.focusIndex = focusIndex >= total - 1 ? 0 : focusIndex + 1;
            this.focusLabel = filteredOptions[this.focusIndex]._label;
            e.preventDefault();
            break;

          default:
            break;
        }
      } else {
        switch (keyCode) {
          case 13:
          case 38:
          case 40:
            this.onTriggerClick();
            e.preventDefault();
            break;
        }
      }
    },
    onOptionEnter(index, optionElems) {
      if (this._isCreateShow && index === 0) {
        this.$refs.createOption.click();
      } else {
        optionElems[index - 1] && optionElems[index - 1].click();
      }
    },
    onTriggerClick() {
      if (
        this.readonly
        || this.disabled
        || (this._isFilteringMode && this.popupShow)
      ) {
        return;
      }
      this.popupShow = !this.popupShow;

      if (this._isFilteringMode && this.clearInputAfterSelect) {
        this.$refs.input.clearText();
      }
    },
    onSuffixIconClick(e) {
      e.stopPropagation();
      if (this.readonly || this.disabled) return;
      this.popupShow = !this.popupShow;
    },
    onClear() {
      this.$emit('change', this.reverse([]));
      this.$nextTick(() => {
        this.formItem && this.formItem.$emit('form.change');
      });
    },
    selectBlur(e) {
      this.popupShow = false;
      this.isBlur = true;
      this.onInputText('');
      /**
       * 失焦
       * @event blur
       * @param {Object} e
       */
      this.$emit('blur', e);
      this.formItem && this.formItem.$emit('form.blur');
    },
    getInput(el) {
      this.refInput = el;
    },
    inputFocus(e) {
      if (this.isBlur) {
        /**
         * 聚焦
         *
         * @event focus
         * @param {Object} e
         */
        this.$emit('focus', e);
        this.isBlur = false;
      }
    },
    /**
     * 聚焦select
     *
     * @public
     */
    focus() {
      if (this.refInput) {
        this.refInput.focus();
      }
    },
    /**
     * 使select失焦
     *
     * @public
     */
    blur() {
      if (this.refInput) {
        this.refInput.blur();
      }
    },
    /**
     * 聚焦select，并弹出选项面板
     *
     * @public
     */
    trigger() {
      if (this.refInput) {
        this.refInput.focus();
        setTimeout(this.onTriggerClick);
      }
    },
    isOptionChecked(option) {
      return this.dataValue && !!this.dataValue.find(v => v && v._value === option._value);
    },
  },
};
